import { useParams, useNavigate, Link, useSearchParams } from 'react-router'
import { EmptyState } from 'src/components/empty-state'
import { AssistantHint } from 'src/components/assistant-hint'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useTnForm, FormProvider } from '@thinknimble/tn-forms-react'
import { useEffect, useRef } from 'react'
import {
  reservationQueries,
  reservationApi,
  ReservationForm,
  TReservationForm,
  ReservationFormInputs,
} from '../services/reservations'
import { ReservationGuest } from '../services/reservation-guests'
import { getGuestSurveyLink } from '../services/guest-survey'
import { useToast } from '../components/toast'
import { Input } from '../components/input'
import { Button } from '../components/button'
import { InfoHint } from '../components/info-hint'
import { ErrorsList } from '../components/errors'
import { useState } from 'react'
import { ReservationGuestCard } from 'src/components/reservation-guest-card'
import { Modal } from 'src/components/modal'
import { toPropertyTzDatetimeLocal, fromPropertyTzToUTC, withTimeOfDay } from 'src/utils/dates'
import { CurrencyInputField } from '../components/currency-input-field'
import { CustomSelect } from '../components/custom-select'
import { providerLabelMap, reservationKindEnum } from 'src/services/reservations/options'
import { propertyQueries } from 'src/services/properties/queries'
import type { Property } from 'src/services/properties/models'
import { PageHeader } from '../components/layout/page-header'
import { MainContent } from '../components/layout/main-content'
import { ContentPane } from '../components/layout/content-pane'
import { Spinner } from '../components/spinner'
import { Switch } from '../components/switch'
import { PrivateBadge } from '../components/reservations/private-badge'
import { useTranslation } from 'react-i18next'
import { ArrowLeftIcon, PlusIcon } from '@heroicons/react/24/outline'
import { ReceiptDisplay } from '../components/reservations/receipt-display'
import { QuickAddGuestModal } from '../components/reservations/quick-add-guest-modal'
import { GuestDataRetentionBanner } from '../components/reservations/guest-data-retention-banner'
import { PurgeGuestDataDialog } from '../components/reservations/purge-guest-data-dialog'
import { RESERVATION_DETAILS_ANCHOR } from '../components/reservations/missing-booking-details-notice'

function formatDateForInput(value: unknown, timezone: string | null | undefined): string {
  if (!value) return ''
  if (typeof value === 'string') {
    if (value.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(value)) {
      return toPropertyTzDatetimeLocal(value, timezone)
    }
    return value
  }
  if (value instanceof Date) {
    return toPropertyTzDatetimeLocal(value.toISOString(), timezone)
  }
  return ''
}

function rawDateValue(value: string | Date | null | undefined): string {
  if (!value) return ''
  return value instanceof Date ? value.toISOString() : value
}

function toUtcIso(value: string, timezone: string | null | undefined): string {
  if (!value) return ''
  if (value.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(value)) {
    return value
  }
  return fromPropertyTzToUTC(value, timezone)
}

export function ReservationDetail() {
  const { t } = useTranslation()
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const {
    data: reservation,
    isLoading,
    refetch,
  } = useQuery({
    ...reservationQueries.retrieve(id!),
    enabled: !!id,
  })
  const { mutate: updateReservation, isPending: isUpdating } = useMutation({
    mutationFn: reservationApi.update,
    onSuccess: () => refetch(),
    onError: (e: any) => setError(e.message || 'Error updating reservation'),
  })

  const [selectedGuest, setSelectedGuest] = useState<ReservationGuest | null>(null)
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false)
  const [showQuickAdd, setShowQuickAdd] = useState(false)
  const [propertySearch, setPropertySearch] = useState('')

  const { data: propertiesResponse, isLoading: isLoadingProperties } = useQuery(
    propertyQueries.list({
      filters: propertySearch ? { search: propertySearch } : undefined,
    }),
  )
  const properties = propertiesResponse?.results ?? []
  const propertyOptions = properties.map((r: Property) => ({
    label: r.name || t('reservations.unnamedProperty'),
    value: r.id,
  }))

  const { mutate: createReservation, isPending: isCreating } = useMutation({
    mutationFn: reservationApi.create,
    onSuccess: (data) => {
      navigate(`/reservations/${data.id}`)
    },
    onError: (e: any) => setError(e.message || 'Error creating reservation'),
  })

  const { toast } = useToast()
  const { mutate: generateGuestLink, isPending: isGeneratingLink } = useMutation({
    mutationFn: () => getGuestSurveyLink(reservation!.id),
    onSuccess: async ({ url }) => {
      try {
        await navigator.clipboard.writeText(url)
        toast({
          type: 'success',
          title: t('reservations.guestSurveyLink'),
          description: t('reservations.guestLinkCopied'),
        })
      } catch {
        // Clipboard may be unavailable (insecure context / denied permission) —
        // surface the URL so it can still be copied by hand.
        toast({ type: 'default', title: t('reservations.guestSurveyLink'), description: url })
      }
    },
    onError: (e: any) =>
      toast({
        type: 'error',
        title: t('reservations.guestSurveyLink'),
        description: e.message || t('reservations.guestLinkError'),
      }),
  })

  const isPending = isUpdating || isCreating

  const { form, createFormFieldChangeHandler, overrideForm } = useTnForm<TReservationForm>()

  // Resolved on its own rather than from `properties`: with server-side search the
  // fetched page narrows as the user types, and the selected property may not be in it.
  const selectedPropertyId = form.property.value || ''
  const { data: fetchedProperty } = useQuery({
    ...propertyQueries.retrieve(selectedPropertyId),
    enabled: Boolean(selectedPropertyId),
  })
  const selectedProperty =
    fetchedProperty ?? properties.find((r: Property) => r.id === selectedPropertyId)
  const selectedPropertyLabel = selectedProperty
    ? selectedProperty.name || t('reservations.unnamedProperty')
    : ''
  const timezone = selectedProperty?.timezone ?? reservation?.propertyTimezone ?? null

  // New reservations and blocks take their times from the property until the user
  // changes a time themselves. Existing reservations keep whatever was stored.
  const defaultTimes = id
    ? null
    : { checkinDate: selectedProperty?.checkInTime, checkoutDate: selectedProperty?.checkOutTime }
  const customTime = useRef({ checkinDate: false, checkoutDate: false })
  const handleDateChange = (key: 'checkinDate' | 'checkoutDate') => (value: string) => {
    const previous = form[key].value
    if (typeof previous === 'string' && previous && previous.slice(11) !== value.slice(11)) {
      customTime.current[key] = true
    }
    const defaultTime = defaultTimes?.[key]
    const next =
      value && defaultTime && !customTime.current[key] ? withTimeOfDay(value, defaultTime) : value
    createFormFieldChangeHandler(form[key])(next)
  }
  useEffect(() => {
    if (!defaultTimes) return
    for (const key of ['checkinDate', 'checkoutDate'] as const) {
      const value = form[key].value
      const defaultTime = defaultTimes[key]
      if (typeof value === 'string' && value && defaultTime && !customTime.current[key]) {
        createFormFieldChangeHandler(form[key])(withTimeOfDay(value, defaultTime))
      }
    }
    // Re-apply only when the selected property (and so its default times) changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTimes?.checkinDate, defaultTimes?.checkoutDate])

  useEffect(() => {
    if (reservation) {
      const value = {
        ...reservation,
        guestPaidPriceFloat: reservation.guestPaidPrice,
        totalPriceFloat: reservation.totalPrice,
      }
      overrideForm(ReservationForm.create(value as any) as TReservationForm)
    } else if (!id) {
      const newForm = new ReservationForm() as TReservationForm
      if (searchParams.get('kind') === reservationKindEnum.block) {
        newForm.kind.value = reservationKindEnum.block
        newForm.declaredNumberOfGuests.value = 0
      }
      const propertyParam = searchParams.get('property')
      if (propertyParam) newForm.property.value = propertyParam
      overrideForm(newForm)
    }
  }, [reservation, overrideForm, id, searchParams])

  if (isLoading && id) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!reservation && id) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4">
        <p className="text-lg text-text-placeholder">{t('reservations.notFound')}</p>
        <Button variant="secondary" onClick={() => navigate('/reservations')}>
          <ArrowLeftIcon className="mr-1 h-4 w-4" aria-hidden="true" />
          {t('reservations.backToList')}
        </Button>
      </div>
    )
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.validate()
    if (!form.isValid) return
    setError(null)
    const formValues = form.formattedValue
    const payload = {
      ...formValues,
      // From the raw input value: `formattedValue` reads it in the browser's timezone.
      checkinDate: toUtcIso(rawDateValue(form.checkinDate.value), timezone),
      checkoutDate: toUtcIso(rawDateValue(form.checkoutDate.value), timezone),
      provider: formValues.provider as 'airbnb' | 'vrbo' | 'manual',
      property: formValues.property ?? '',
    }
    if (id && reservation) {
      updateReservation({ id: reservation.id, ...payload })
    } else {
      createReservation(payload)
    }
  }

  const guests = reservation?.reservationGuestsRef || []
  const isBlock = form.kind.value === reservationKindEnum.block
  const hasUnscrubbedGuests = guests.some((g) => !g.dataScrubbed)

  return (
    <>
      <PageHeader>
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-primary">
            {isBlock
              ? id
                ? t('reservations.editBlockTitle')
                : t('reservations.createBlockTitle')
              : id
              ? t('reservations.editTitle')
              : t('reservations.createTitle')}
            {reservation?.isPrivate && <PrivateBadge />}
          </h1>
          {id && selectedPropertyId && (
            <p className="mt-1 text-sm text-text-secondary" data-testid="reservation-property">
              {t('reservations.propertyLabel')}:{' '}
              <Link
                to={`/properties/${selectedPropertyId}`}
                className="font-medium text-primary hover:underline"
              >
                {selectedPropertyLabel || selectedPropertyId}
              </Link>
            </p>
          )}
        </div>
      </PageHeader>

      <MainContent>
        {/* Reservation Form + Receipt */}
        <div className="flex min-w-0 flex-1 flex-col gap-6">
          <div
            id={RESERVATION_DETAILS_ANCHOR}
            className="scroll-mt-4 rounded-xl border border-border bg-surface p-4 shadow-sm sm:p-6"
          >
            <h2 className="mb-4 text-xl font-bold sm:mb-6">
              {isBlock ? t('reservations.blockDetails') : t('reservations.reservationDetails')}
              {isBlock && <InfoHint text={t('reservations.blockHelp')} className="ml-1.5" />}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <fieldset disabled={isPending} className="space-y-4">
                {/* Confirmation Code & Guests */}
                {!isBlock && (
                  <div className="grid grid-cols-1 gap-4 @[500px]:grid-cols-2">
                    <div>
                      <Input
                        label={form.providerConfirmationCode.label}
                        placeholder={form.providerConfirmationCode.placeholder}
                        value={form.providerConfirmationCode.value ?? ''}
                        onChange={(e) =>
                          createFormFieldChangeHandler(form.providerConfirmationCode)(
                            e.target.value,
                          )
                        }
                      />
                      <ErrorsList errors={form.providerConfirmationCode.errors} />
                    </div>
                    <div>
                      <Input
                        label={form.declaredNumberOfGuests.label}
                        placeholder={form.declaredNumberOfGuests.placeholder}
                        type={form.declaredNumberOfGuests.type}
                        value={form.declaredNumberOfGuests.value ?? ''}
                        onChange={(e) =>
                          createFormFieldChangeHandler(form.declaredNumberOfGuests)(
                            Number(e.target.value),
                          )
                        }
                      />
                      <ErrorsList errors={form.declaredNumberOfGuests.errors} />
                    </div>
                  </div>
                )}

                {/* Dates */}
                <div className="grid grid-cols-1 gap-4 @[500px]:grid-cols-2">
                  <div>
                    <Input
                      label={form.checkinDate.label}
                      placeholder={form.checkinDate.placeholder}
                      type={form.checkinDate.type}
                      value={formatDateForInput(form.checkinDate.value, timezone)}
                      onChange={(e) => handleDateChange('checkinDate')(e.target.value)}
                    />
                    <ErrorsList errors={form.checkinDate.errors} />
                  </div>
                  <div>
                    <Input
                      label={form.checkoutDate.label}
                      placeholder={form.checkoutDate.placeholder}
                      type={form.checkoutDate.type}
                      value={formatDateForInput(form.checkoutDate.value, timezone)}
                      onChange={(e) => handleDateChange('checkoutDate')(e.target.value)}
                    />
                    <ErrorsList errors={form.checkoutDate.errors} />
                  </div>
                </div>

                {/* Prices & Provider — a block carries no booking details */}
                {!isBlock && (
                  <>
                    <div className="grid grid-cols-1 gap-4 @[500px]:grid-cols-2">
                      <div>
                        <CurrencyInputField
                          label={form.totalPrice.label}
                          value={form.totalPrice.value}
                          currency={form.totalPriceCurrency.value ?? ''}
                          onValueChange={(val) => {
                            createFormFieldChangeHandler(form.totalPrice)(val?.value ?? 0)
                            createFormFieldChangeHandler(form.totalPriceFloat)(val?.float ?? 0)
                          }}
                          onCurrencyChange={(val) =>
                            createFormFieldChangeHandler(form.totalPriceCurrency)(val)
                          }
                          placeholder={form.totalPrice.placeholder}
                          currencyLabel={form.totalPriceCurrency.label}
                          id="total-price"
                          name="totalPrice"
                        />
                        <ErrorsList errors={form.totalPrice.errors} />
                        <ErrorsList errors={form.totalPriceCurrency.errors} />
                      </div>
                      <div>
                        <CurrencyInputField
                          label={form.guestPaidPrice.label}
                          value={form.guestPaidPrice.value}
                          currency={form.guestPaidPriceCurrency.value ?? ''}
                          onValueChange={(val) => {
                            createFormFieldChangeHandler(form.guestPaidPrice)(val?.value ?? 0)
                            createFormFieldChangeHandler(form.guestPaidPriceFloat)(val?.float ?? 0)
                          }}
                          onCurrencyChange={(val) =>
                            createFormFieldChangeHandler(form.guestPaidPriceCurrency)(val)
                          }
                          placeholder={form.guestPaidPrice.placeholder}
                          currencyLabel={form.guestPaidPriceCurrency.label}
                          id="guest-paid-price"
                          name="guestPaidPrice"
                        />
                        <ErrorsList errors={form.guestPaidPrice.errors} />
                        <ErrorsList errors={form.guestPaidPriceCurrency.errors} />
                      </div>
                    </div>

                    {/* Provider */}
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        {t('reservationForm.providerLbl')}
                      </label>
                      <CustomSelect
                        clearable
                        values={
                          form.provider.value
                            ? [
                                {
                                  label:
                                    providerLabelMap[
                                      form.provider.value as keyof typeof providerLabelMap
                                    ],
                                  value: form.provider.value,
                                },
                              ]
                            : []
                        }
                        onChange={(val) => {
                          if (val) {
                            createFormFieldChangeHandler(form.provider)(val[0]?.value)
                          }
                        }}
                        options={Object.entries(providerLabelMap).map(([key, value]) => ({
                          label: value,
                          value: key,
                        }))}
                        name="provider"
                      />
                      <ErrorsList errors={form.provider.errors} />
                    </div>
                  </>
                )}

                {/* Property */}
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    {form.property.label}
                  </label>
                  <CustomSelect
                    values={
                      selectedPropertyId
                        ? [
                            {
                              label: selectedPropertyLabel || selectedPropertyId,
                              value: selectedPropertyId,
                            },
                          ]
                        : []
                    }
                    onChange={(val) => {
                      if (val) {
                        createFormFieldChangeHandler(form.property)(val[0]?.value)
                      }
                    }}
                    options={propertyOptions}
                    name="property"
                    placeholder={form.property.placeholder}
                    searchable
                    searchFn={({ state }) => {
                      setPropertySearch(state.search)
                      return propertyOptions
                    }}
                    loading={isLoadingProperties}
                  />
                  <ErrorsList errors={form.property.errors} />
                </div>

                {/* Private — kept off the calendar.ics export feed */}
                <div className="flex items-start gap-3">
                  <Switch
                    id="is-private"
                    checked={Boolean(form.isPrivate.value)}
                    onCheckedChange={(checked) =>
                      createFormFieldChangeHandler(form.isPrivate)(checked)
                    }
                    className="mt-0.5"
                    data-testid="private-toggle"
                  />
                  <label htmlFor="is-private" className="text-sm">
                    <span className="block font-medium text-gray-700">{form.isPrivate.label}</span>
                  </label>
                  <InfoHint text={t('reservationForm.privateHelp')} data-testid="private-help" />
                </div>

                {/* Notes */}
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    {form.notes.label}
                  </label>
                  <textarea
                    className="w-full rounded-md border bg-surface p-2 text-text-primary focus:ring-2 focus:ring-border-strong disabled:cursor-not-allowed disabled:opacity-50"
                    placeholder={form.notes.placeholder}
                    value={form.notes.value || ''}
                    onChange={(e) => createFormFieldChangeHandler(form.notes)(e.target.value)}
                    rows={4}
                  />
                  <ErrorsList errors={form.notes.errors} />
                </div>
              </fieldset>

              {error && (
                <div className="rounded-md bg-error-50 p-3 text-sm text-error-600">{error}</div>
              )}

              <Button type="submit" className="w-full" variant="primary" disabled={isPending}>
                {isPending ? (
                  <span className="flex items-center justify-center gap-2">
                    <Spinner size="xs" />
                    {t('common.saving')}
                  </span>
                ) : isBlock ? (
                  id ? (
                    t('reservations.updateBlockBtn')
                  ) : (
                    t('reservations.createBlockBtn')
                  )
                ) : id ? (
                  t('reservations.updateBtn')
                ) : (
                  t('reservations.createBtn')
                )}
              </Button>
            </form>
          </div>

          {id && !isBlock && reservation?.receipt && reservation.receipt.total !== undefined && (
            <ReceiptDisplay
              key={reservation.id}
              receipt={reservation.receipt}
              booker={reservation.reservationGuestsRef?.find((guest) => guest.isBooker)}
              currency={reservation.totalPriceCurrency}
            />
          )}
        </div>

        {/* Guests Section */}
        {id && reservation && !isBlock && (
          <div className="min-w-0 flex-1">
            {/* Section header (not a page title): the page's h1 is the reservation form's. */}
            <ContentPane>
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-surface p-4 shadow-sm">
                <h2 className="text-xl font-bold">{t('reservations.guestsSection')}</h2>
                <Button
                  variant="primary"
                  icon={<PlusIcon />}
                  onClick={() => setShowQuickAdd(true)}
                  data-testid="add-guest-button"
                >
                  {t('reservations.addGuest')}
                </Button>
              </div>
              {hasUnscrubbedGuests && (
                <GuestDataRetentionBanner checkoutDate={reservation.checkoutDate} />
              )}
              {hasUnscrubbedGuests && (
                <div className="flex justify-end">
                  <Button
                    variant="danger"
                    onClick={() => setShowPurgeConfirm(true)}
                    className="text-sm"
                    data-testid="purge-guest-data-button"
                  >
                    {t('reservations.purgeGuestData')}
                  </Button>
                </div>
              )}
              {guests.length === 0 ? (
                <div className="rounded-xl border border-border bg-surface p-4">
                  <EmptyState
                    title={t('reservations.noGuestsYet')}
                    assistantHint={
                      <AssistantHint id="reservation-guests" text={t('assistantHint.guests')} />
                    }
                    data-testid="no-guests-empty-state"
                  />
                </div>
              ) : (
                <div className="max-h-[70vh] space-y-3 overflow-y-auto">
                  {guests.map((guest: ReservationGuest) => (
                    <ReservationGuestCard
                      key={guest.id}
                      guest={guest}
                      reservationId={reservation.id}
                      checkoutDate={reservation.checkoutDate}
                      onShowTra={setSelectedGuest}
                      reportsTra={reservation.reportsTra}
                      reportsSire={reservation.reportsSire}
                      propertyCountry={reservation.propertyCountry}
                      missingBookingDetails={reservation.missingBookingDetails}
                    />
                  ))}
                </div>
              )}
            </ContentPane>
          </div>
        )}
      </MainContent>

      {reservation && (
        <PurgeGuestDataDialog
          isOpen={showPurgeConfirm}
          reservationId={reservation.id}
          guests={guests}
          onClose={() => setShowPurgeConfirm(false)}
          onError={setError}
        />
      )}

      <QuickAddGuestModal
        reservationId={reservation?.id ?? ''}
        isOpen={showQuickAdd}
        onClose={() => setShowQuickAdd(false)}
        onCopySurveyLink={() => generateGuestLink()}
        isCopyingSurveyLink={isGeneratingLink}
      />

      <Modal title="" isOpen={!!selectedGuest} onClose={() => setSelectedGuest(null)}>
        {selectedGuest?.traData && (
          <pre className="max-h-96 overflow-auto rounded-md bg-gray-100 p-4 text-sm">
            <code>{JSON.stringify(selectedGuest.traData, null, 2)}</code>
          </pre>
        )}
      </Modal>
    </>
  )
}

export const ReservationDetailPage = () => {
  return (
    <FormProvider<ReservationFormInputs> formClass={ReservationForm}>
      <ReservationDetail />
    </FormProvider>
  )
}
